import { profileClient } from '@/clients/profile-client.js';
import { backboneService } from '@/services/backbone.js';
import { toastService } from '@/services/toast.js';
import { HatElement } from '@/components/hat-element.js';

class HatUserProfile extends HatElement {
    private _userName = '';
    private _editing = false;

    connectedCallback() {
    super.connectedCallback();
    this._renderShell();
        this._load();
    }

    async _load() {
        try {
            const profile = await profileClient.getProfile();
            this._userName = profile.userName;
            this._renderCard();
        } catch {
            toastService.show('Failed to load profile.');
        }
    }

    _renderShell() {
        this.root.innerHTML = `
            <style>
                .page-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 24px;
                }

                .page-title {
                    color: white;
                    font-size: 1.4rem;
                    font-weight: 700;
                    margin: 0;
                    text-shadow: 0 2px 4px rgba(0,0,0,0.3);
                }

                .card {
                    background: white;
                    border-radius: 16px;
                    padding: 28px;
                    max-width: 480px;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.12);
                }

                .field-label {
                    display: block;
                    font-size: 0.78rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    color: #666;
                    margin-bottom: 6px;
                }

                .field-value {
                    font-size: 1rem;
                    color: #111;
                    padding: 2px 0;
                }

                .field {
                    margin-bottom: 20px;
                }

                input[type="text"] {
                    width: 100%;
                    padding: 10px 12px;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    font-size: 1rem;
                    box-sizing: border-box;
                    transition: border-color 0.15s;
                    font-family: inherit;
                }

                input[type="text"]:focus {
                    outline: none;
                    border-color: #764ba2;
                }

                .divider {
                    border: none;
                    border-top: 1px solid #f0f0f0;
                    margin: 20px 0 20px;
                }

                .actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 10px;
                }

                .btn-primary {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    border: none;
                    padding: 10px 24px;
                    border-radius: 8px;
                    font-size: 0.95rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: opacity 0.15s;
                }

                .btn-primary:hover:not(:disabled) { opacity: 0.88; }

                .btn-primary:disabled {
                    opacity: 0.45;
                    cursor: default;
                }

                .btn-secondary {
                    background: none;
                    border: 1px solid #ddd;
                    color: #555;
                    padding: 10px 20px;
                    border-radius: 8px;
                    font-size: 0.95rem;
                    cursor: pointer;
                    transition: border-color 0.15s, color 0.15s;
                }

                .btn-secondary:hover { border-color: #999; color: #333; }

                .btn-back {
                    background: rgba(255,255,255,0.15);
                    border: 1px solid rgba(255,255,255,0.4);
                    color: white;
                    padding: 7px 16px;
                    border-radius: 8px;
                    font-size: 0.88rem;
                    cursor: pointer;
                    transition: background 0.15s;
                }

                .btn-back:hover { background: rgba(255,255,255,0.25); }

                .loading {
                    color: #999;
                    font-size: 0.95rem;
                }
            </style>

            <div class="page-header">
                <h2 class="page-title">My Profile</h2>
                <button class="btn-back">← Back to App</button>
            </div>
            <div class="card"><p class="loading">Loading…</p></div>
        `;

        this.root.querySelector<HTMLButtonElement>('.btn-back')!
            .addEventListener('click', () => backboneService.navigateTo('/app'));
    }

    _renderCard() {
        const card = this.root.querySelector('.card')!;

        if (this._editing) {
            card.innerHTML = `
                <div class="field">
                    <span class="field-label">Display Name</span>
                    <input id="user-name" type="text" value="${this._escapeAttr(this._userName)}" autocomplete="name" />
                </div>
                <hr class="divider" />
                <div class="actions">
                    <button class="btn-secondary btn-cancel">Cancel</button>
                    <button class="btn-primary btn-save" disabled>Save</button>
                </div>
            `;

            const input = card.querySelector<HTMLInputElement>('#user-name')!;
            const saveBtn = card.querySelector<HTMLButtonElement>('.btn-save')!;

            input.focus();

            input.addEventListener('input', () => {
                saveBtn.disabled = input.value.trim() === this._userName;
            });

            card.querySelector('.btn-cancel')!.addEventListener('click', () => {
                this._editing = false;
                this._renderCard();
            });

            saveBtn.addEventListener('click', async () => {
                const newName = input.value.trim();
                if (!newName) return;

                saveBtn.disabled = true;
                saveBtn.textContent = 'Saving…';

                try {
                    const updated = await profileClient.updateProfile(newName);
                    this._userName = updated.userName;
                    this._editing = false;
                    toastService.show('Profile saved.', 'success');
                    backboneService.navigateTo('/app');
                } catch {
                    toastService.show('Failed to save profile.');
                    saveBtn.disabled = false;
                    saveBtn.textContent = 'Save';
                }
            });
        } else {
            card.innerHTML = `
                <div class="field">
                    <span class="field-label">Display Name</span>
                    <div class="field-value">${this._escape(this._userName)}</div>
                </div>
                <hr class="divider" />
                <div class="actions">
                    <button class="btn-primary btn-edit">Edit</button>
                </div>
            `;

            card.querySelector('.btn-edit')!.addEventListener('click', () => {
                this._editing = true;
                this._renderCard();
            });
        }
    }

    _escape(value: string): string {
        return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    _escapeAttr(value: string): string {
        return value.replace(/"/g, '&quot;');
    }
}

customElements.define('hat-user-profile', HatUserProfile);